// The currencies the ledger knows (as db/016 and db/020), for pickers. Forms
// list the books' own currency and the ones the business deals in first
// (Settings → Currencies), then every other currency by region.

export type Region = 'europe' | 'americas' | 'asia_pacific' | 'middle_east_africa' | 'other';
export interface CurrencyInfo { code: string; name: string; region: Region; ecb_daily: boolean; minor_units: number }

const c = (code: string, name: string, region: Region, ecb_daily: boolean, minor_units = 2): CurrencyInfo =>
  ({ code, name, region, ecb_daily, minor_units });

export const CURRENCIES: CurrencyInfo[] = [
  // Europe
  c('EUR', 'Euro', 'europe', false), c('GBP', 'Pound sterling', 'europe', true), c('CHF', 'Swiss franc', 'europe', true),
  c('SEK', 'Swedish krona', 'europe', true), c('NOK', 'Norwegian krone', 'europe', true), c('DKK', 'Danish krone', 'europe', true),
  c('ISK', 'Icelandic króna', 'europe', true, 0), c('PLN', 'Polish złoty', 'europe', true), c('CZK', 'Czech koruna', 'europe', true),
  c('HUF', 'Hungarian forint', 'europe', true), c('RON', 'Romanian leu', 'europe', true), c('TRY', 'Turkish lira', 'europe', true),
  c('RSD', 'Serbian dinar', 'europe', false), c('UAH', 'Ukrainian hryvnia', 'europe', false),
  c('BAM', 'Bosnia and Herzegovina convertible mark', 'europe', false), c('MKD', 'Macedonian denar', 'europe', false),
  c('ALL', 'Albanian lek', 'europe', false), c('MDL', 'Moldovan leu', 'europe', false), c('GEL', 'Georgian lari', 'europe', false),
  // The Americas
  c('USD', 'US dollar', 'americas', true), c('CAD', 'Canadian dollar', 'americas', true), c('MXN', 'Mexican peso', 'americas', true),
  c('BRL', 'Brazilian real', 'americas', true), c('CLP', 'Chilean peso', 'americas', false, 0), c('COP', 'Colombian peso', 'americas', false),
  c('ARS', 'Argentine peso', 'americas', false),
  // Asia and the Pacific
  c('CNY', 'Chinese yuan', 'asia_pacific', true), c('JPY', 'Japanese yen', 'asia_pacific', true, 0), c('HKD', 'Hong Kong dollar', 'asia_pacific', true),
  c('TWD', 'New Taiwan dollar', 'asia_pacific', false), c('KRW', 'South Korean won', 'asia_pacific', true, 0),
  c('SGD', 'Singapore dollar', 'asia_pacific', true), c('INR', 'Indian rupee', 'asia_pacific', true), c('VND', 'Vietnamese đồng', 'asia_pacific', false, 0),
  c('THB', 'Thai baht', 'asia_pacific', true), c('MYR', 'Malaysian ringgit', 'asia_pacific', true), c('IDR', 'Indonesian rupiah', 'asia_pacific', true),
  c('PHP', 'Philippine peso', 'asia_pacific', true), c('BDT', 'Bangladeshi taka', 'asia_pacific', false), c('PKR', 'Pakistani rupee', 'asia_pacific', false),
  c('AUD', 'Australian dollar', 'asia_pacific', true), c('NZD', 'New Zealand dollar', 'asia_pacific', true),
  // The Middle East and Africa
  c('AED', 'UAE dirham', 'middle_east_africa', false), c('SAR', 'Saudi riyal', 'middle_east_africa', false),
  c('ILS', 'Israeli new shekel', 'middle_east_africa', true), c('EGP', 'Egyptian pound', 'middle_east_africa', false),
  c('MAD', 'Moroccan dirham', 'middle_east_africa', false), c('ZAR', 'South African rand', 'middle_east_africa', true),
];

export const CURRENCY_NAMES: Record<string, string> = Object.fromEntries(CURRENCIES.map((x) => [x.code, x.name]));

export const REGION_LABEL: Record<Region, string> = {
  europe: 'Europe', americas: 'The Americas', asia_pacific: 'Asia and the Pacific', middle_east_africa: 'Middle East and Africa', other: 'Other',
};

/** Groups for a picker: the books' own currency and the business's chosen ones first, then the rest by region. */
export function currencyGroups(base: string, chosen: string[] = []): { label: string; options: CurrencyInfo[] }[] {
  const known = (code: string) => CURRENCIES.find((x) => x.code === code) ?? c(code, code, 'other', false);
  const first = [base, ...chosen.filter((x) => x !== base)].map(known);
  const taken = new Set(first.map((x) => x.code));
  const rest = (Object.keys(REGION_LABEL) as Region[])
    .map((r) => ({ label: REGION_LABEL[r], options: CURRENCIES.filter((x) => x.region === r && !taken.has(x.code)).sort((a, b) => a.name.localeCompare(b.name)) }))
    .filter((g) => g.options.length > 0);
  return [{ label: 'Your currencies', options: first }, ...rest];
}

/** Flat list, for places that don't group (the commonest first). */
export const CURRENCY_OPTIONS: { code: string; name: string }[] = currencyGroups('EUR', ['GBP', 'USD']).flatMap((g) => g.options);
